import { useState, useEffect, useMemo, useRef } from "react";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext"; // ← match your AuthContext path
import { BellSchedulePreview } from "./BellSchedule"; // ← same folder as this editor

// ─── CONFIG ───────────────────────────────────────────────────────────────────
// `division` and `toolId` come in as props (set on the routes in App.js) so this
// one component serves both Upper and Middle School with fully independent access
// control. Defaults preserve the original Upper School behavior if no props pass.

const KA = {
  red:"#E83530", navy:"#233C5F", navyDark:"#152540", gold:"#A29164",
  goldLight:"#c4b48a", cream:"#F5F0E8", white:"#FFFFFF", offWhite:"#FAF8F4",
  green:"#2E7D5B", errBg:"#FCEBEA", errBorder:"#E8A5A2",
};

const TYPES = ["class", "break", "advisory", "meeting", "lunch", "hi", "free"];
const MENU_CATEGORIES = ["Entrée", "Vegetarian", "Salad", "Soup", "Vegetable", "Side", "Dessert", "Beverage", "Special"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const nowAmman = () => new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Amman" }));
const dstr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
// Sunday of the school week. On Fri/Sat, jump forward to the upcoming Sunday so
// the editor opens on next week (ready to fill); Sun–Thu → that week's Sunday.
function weekSundayOf(date) {
  const dow = date.getDay();
  const shift = dow === 5 ? 2 : dow === 6 ? 1 : -dow;
  return addDays(date, shift);
}
const DOW_LABELS = { "0":"Sunday", "1":"Monday", "2":"Tuesday", "3":"Wednesday", "4":"Thursday" };

// Each editor section belongs to one publish scope. A publish writes ONLY that
// scope's fields, so two people editing different components can't overwrite each
// other regardless of publish order.
const SCOPE_OF = { weekly:"schedule", noschool:"schedule", special:"schedule", advanced:"schedule", announce:"announcements", menu:"lunch", speaker:"speaker", event:"event" };
const SCOPE_LABEL = { schedule:"schedule", announcements:"announcements", lunch:"lunch menu", speaker:"featured speaker", event:"event mode" };

const clone = typeof structuredClone === "function"
  ? structuredClone
  : (o) => JSON.parse(JSON.stringify(o));

// Rich-text helpers (announcements).
const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]));
const stripTags = (h) => String(h || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
const splitDT = (s) => { const [d, t] = String(s || "").split("T"); return [d || "", t || "00:00"]; };
const joinDT  = (d, t) => `${d || ""}T${t || "00:00"}`;
const DTRE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

// Downscale an image file to a small JPEG data URL, so a speaker photo (or an
// event flyer) can live inline in the config doc (no Firebase Storage needed) and
// load instantly on unauthenticated kiosks. The default ~560px keeps a speaker
// photo well under Firestore's 1 MB limit; callers pass a larger max for flyers.
function fileToResizedDataUrl(file, maxDim = 560, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      try { resolve(c.toDataURL("image/jpeg", quality)); } catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

// ─── VALIDATION (same rules as the seed script) ────────────────────────────────
function toMins(t) {
  const m = /^(\d{2}):(\d{2})$/.exec(t || "");
  if (!m) return null;
  const h = +m[1], min = +m[2];
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
function validatePeriods(label, periods, cfg) {
  const errs = [];
  const bpd = cfg.meta?.blocksPerDay ?? 5;
  let prevEnd = -1, prevName = null;
  (periods || []).forEach((p) => {
    const s = toMins(p.start), e = toMins(p.end);
    if (s === null) errs.push(`${label}: "${p.label || "(unnamed)"}" — invalid start "${p.start}"`);
    if (e === null) errs.push(`${label}: "${p.label || "(unnamed)"}" — invalid end "${p.end}"`);
    if (s !== null && e !== null && e <= s)
      errs.push(`${label}: "${p.label}" ends at or before it starts`);
    if (s !== null && prevEnd !== -1 && s < prevEnd)
      errs.push(`${label}: "${p.label}" overlaps the previous period "${prevName}"`);
    if (e !== null) { prevEnd = e; prevName = p.label; }
    if (p.type === "class") {
      const hasSlot = Number.isInteger(p.slot) && p.slot >= 0 && p.slot < bpd;
      const hasLetter = typeof p.blockLetter === "string" && (cfg.rotation?.letters || []).includes(p.blockLetter);
      if (!hasSlot && !hasLetter)
        errs.push(`${label}: class "${p.label}" needs a rotation slot or a fixed letter`);
    }
  });
  return errs;
}
// Returns tagged errors: { scope, msg }. A publish is blocked only by errors in
// its own scope, so a broken announcement never blocks a lunch publish.
function validateConfig(cfg) {
  const errs = [];
  const S = (msg) => errs.push({ scope: "schedule", msg });
  const A = (msg) => errs.push({ scope: "announcements", msg });
  const L = (msg) => errs.push({ scope: "lunch", msg });
  const F = (msg) => errs.push({ scope: "speaker", msg });
  const E = (msg) => errs.push({ scope: "event", msg });
  if (!cfg) return [{ scope: "schedule", msg: "No configuration loaded." }];
  const bpd = cfg.meta?.blocksPerDay ?? 5;
  const letters = cfg.rotation?.letters || [];
  (cfg.rotation?.days || []).forEach((row, i) => {
    if ((row || "").length !== bpd) S(`Rotation Day ${i + 1} must have exactly ${bpd} letters`);
    for (const l of row || []) if (!letters.includes(l)) S(`Rotation Day ${i + 1} uses unknown letter "${l}"`);
  });
  for (const dow of Object.keys(cfg.weekdayTemplates || {}))
    validatePeriods(DOW_LABELS[dow] || `Day ${dow}`, cfg.weekdayTemplates[dow], cfg).forEach(S);
  for (const [date, ov] of Object.entries(cfg.overrides || {})) {
    validatePeriods(`Special day ${date}`, ov.periods, cfg).forEach(S);
    const nDays = (cfg.rotation?.days || []).length;
    if (ov.rotationDay && (ov.rotationDay < 1 || ov.rotationDay > nDays))
      S(`Special day ${date}: rotation day ${ov.rotationDay} is out of range (1–${nDays})`);
  }
  (cfg.noSchool || []).forEach((r, i) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.start || "")) S(`No-school row ${i + 1}: invalid start date`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.end || "")) S(`No-school row ${i + 1}: invalid end date`);
    if (r.start && r.end && r.end < r.start) S(`No-school row ${i + 1}: end is before start`);
  });
  const ann = cfg.announcements;
  if (ann && ann.enabled) {
    (ann.entries || []).forEach((e, i) => {
      const n = i + 1;
      if (!stripTags(e.html)) A(`Announcement #${n} has no message text.`);
      if (!DTRE.test(e.start || "")) A(`Announcement #${n} has an invalid start date/time.`);
      if (!DTRE.test(e.end || "")) A(`Announcement #${n} has an invalid end date/time.`);
      if (DTRE.test(e.start || "") && DTRE.test(e.end || "") && e.end <= e.start)
        A(`Announcement #${n} ends at or before it starts.`);
    });
  }
  const m = cfg.lunchMenu;
  if (m && m.linked === false && m.enabled) {
    const any = Object.values(m.byDate || {}).some((d) => (d?.items || []).some((it) => (it.text || "").trim()));
    if (!any) L("This division's lunch menu is enabled but no day has any items.");
  }
  const feat = cfg.featured;
  if (feat && feat.enabled) {
    if (!(feat.name || "").trim() && !feat.photo) F("Featured speaker is on but has no name or photo.");
    if (feat.showUntil && !DTRE.test(feat.showUntil)) F("Featured speaker auto-hide date/time is invalid.");
  }
  const em = cfg.eventMode;
  if (em && em.enabled) {
    const hasEvents = (em.events || []).some((ev) => (ev?.title || "").trim());
    const hasPoster = !!(em.poster && String(em.poster).trim());
    if (!hasEvents && !hasPoster) E("Event mode is on but has no events or poster.");
    (em.events || []).forEach((ev, i) => {
      const n = i + 1;
      if ((ev?.start || "") && toMins(ev.start) === null) E(`Event #${n} has an invalid start time.`);
      if ((ev?.end || "") && toMins(ev.end) === null) E(`Event #${n} has an invalid end time.`);
      if ((ev?.start || "") && (ev?.end || "") && toMins(ev.start) !== null && toMins(ev.end) !== null && toMins(ev.end) <= toMins(ev.start))
        E(`Event #${n} ends at or before it starts.`);
      if ((ev?.date || "") && !/^\d{4}-\d{2}-\d{2}$/.test(ev.date)) E(`Event #${n} has an invalid date.`);
    });
  }
  return errs;
}

// ─── ROOT ───────────────────────────────────────────────────────────────────
export default function BellScheduleEditor({ division = "upperSchool", toolId = "bellScheduleAdmin" }) {
  const auth = useAuth() || {};
  const { user, isSuperAdmin } = auth;
  // Adjust this to your AuthContext shape if allowedTools lives elsewhere.
  const allowedTools = user?.allowedTools || auth.allowedTools || [];
  const canEdit = isSuperAdmin || allowedTools.includes(toolId);

  const [config, setConfig] = useState(null);
  const [sharedMenu, setSharedMenu] = useState(null);   // shared, school-wide lunch menu
  const [sharedDirty, setSharedDirty] = useState(false); // did we edit the shared menu?
  const [status, setStatus] = useState("loading"); // loading | ready | missing | error
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [section, setSection] = useState("weekly");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [previewOn, setPreviewOn] = useState(false);
  const [previewDate, setPreviewDate] = useState(() => dstr(nowAmman()));
  const [previewTime, setPreviewTime] = useState(() => { const n = nowAmman(); return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`; });

  async function load() {
    setStatus("loading");
    try {
      const snap = await getDoc(doc(db, "bellSchedules", division));
      if (!snap.exists()) { setStatus("missing"); return; }
      const raw = snap.data();
      const ts = raw.meta?.updatedAt;
      setLastUpdated(ts?.toDate ? ts.toDate() : null);
      setLastUpdatedBy(raw.meta?.updatedBy || null);
      const editable = { ...raw, meta: { ...raw.meta } };
      delete editable.meta.updatedAt; // strip Timestamp so state stays cloneable
      // Normalize announcements to the by-entry planner model, migrating the
      // legacy single-message object if that's all the doc has.
      if (!editable.announcements) {
        const la = editable.announcement;
        const today = dstr(nowAmman());
        editable.announcements = {
          enabled: !!(la && la.enabled),
          entries: (la && (la.text || "").trim()) ? [{
            id: "a" + Date.now(),
            html: escapeHtml(la.text).replace(/\n/g, "<br>"),
            style: la.style || "info",
            start: today + "T00:00",
            end: (la.expiresAt || today) + "T23:59",
          }] : [],
        };
      }
      delete editable.announcement; // retire the legacy field
      if (editable.announcements) delete editable.announcements.updatedAt; // strip Timestamps (clone-safety)
      if (editable.lunchMenu) delete editable.lunchMenu.updatedAt;
      if (editable.featured) delete editable.featured.updatedAt;
      if (editable.eventMode) delete editable.eventMode.updatedAt;
      setConfig(editable);
      // Shared, school-wide lunch menu (its own doc).
      const sharedSnap = await getDoc(doc(db, "bellSchedules", "sharedLunch"));
      const sharedRaw = sharedSnap.exists() ? sharedSnap.data() : { enabled: false, byDate: {} };
      delete sharedRaw.updatedAt; // strip Timestamp
      setSharedMenu(sharedRaw);
      setSharedDirty(false);
      setStatus("ready");
    } catch (e) {
      console.error(e); setStatus("error");
    }
  }
  useEffect(() => { if (canEdit) load(); /* eslint-disable-next-line */ }, [canEdit, division]);

  const errors = useMemo(() => validateConfig(config), [config]);
  const previewNow = useMemo(() => {
    const [y, mo, d] = (previewDate || "").split("-").map(Number);
    const [hh, mm] = (previewTime || "12:00").split(":").map(Number);
    if (!y || !mo || !d) return null;
    return new Date(y, mo - 1, d, hh || 0, mm || 0, 0);
  }, [previewDate, previewTime]);

  const scope = SCOPE_OF[section] || "schedule";
  const scopeLabel = SCOPE_LABEL[scope];
  const scopedErrors = errors.filter((e) => e.scope === scope);
  const otherErrors  = errors.filter((e) => e.scope !== scope);

  // immutable update: clone → mutate → set
  const update = (mutator) => setConfig((prev) => { const next = clone(prev); mutator(next); return next; });
  const updateShared = (mutator) => { setSharedDirty(true); setSharedMenu((prev) => { const next = clone(prev || { enabled: false, byDate: {} }); mutator(next); return next; }); };

  // Publish ONE component, writing only its fields (Firestore field-level writes),
  // so it never overwrites another section — even if someone else is editing that
  // section at the same time and publishes right after.
  async function publishScope(sc) {
    if (validateConfig(config).some((e) => e.scope === sc)) return; // block only on this scope's errors
    if (!window.confirm(`Publish the ${SCOPE_LABEL[sc]}? It goes live on the hallway displays within seconds.`)) return;
    setSaving(true);
    try {
      const divRef = doc(db, "bellSchedules", division);
      const by = user?.email || user?.uid || "editor";
      if (sc === "schedule") {
        const c = clone(config);
        await updateDoc(divRef, {
          meta: { ...c.meta, published: true, updatedBy: by, updatedAt: serverTimestamp() },
          year: c.year || {},
          rotation: c.rotation || {},
          noSchool: c.noSchool || [],
          overrides: c.overrides || {},
          weekdayTemplates: c.weekdayTemplates || {},
        });
      } else if (sc === "announcements") {
        const ann = clone(config.announcements || { enabled: false, entries: [] });
        ann.updatedBy = by; ann.updatedAt = serverTimestamp();
        await updateDoc(divRef, { announcements: ann }); // only the announcements field
      } else if (sc === "lunch") {
        const linked = config.lunchMenu?.linked !== false;
        if (linked) {
          const sm = clone(sharedMenu || { enabled: false, byDate: {} });
          sm.updatedBy = by; sm.updatedAt = serverTimestamp();
          await setDoc(doc(db, "bellSchedules", "sharedLunch"), sm); // shared doc only
          await updateDoc(divRef, { "lunchMenu.linked": true });     // + just the link flag
          setSharedDirty(false);
        } else {
          const lm = clone(config.lunchMenu || {});
          lm.linked = false; lm.updatedBy = by; lm.updatedAt = serverTimestamp();
          await updateDoc(divRef, { lunchMenu: lm }); // only this division's own menu
        }
      } else if (sc === "speaker") {
        const feat = clone(config.featured || {});
        feat.updatedBy = by; feat.updatedAt = serverTimestamp();
        await updateDoc(divRef, { featured: feat }); // only the featured-speaker field
      } else if (sc === "event") {
        const em = clone(config.eventMode || {});
        em.updatedBy = by; em.updatedAt = serverTimestamp();
        await updateDoc(divRef, { eventMode: em }); // only the event-mode field
      }
      setLastUpdated(new Date());
      setLastUpdatedBy(by);
      setToast({ kind: "ok", msg: `Published the ${SCOPE_LABEL[sc]} — displays will update shortly.` });
    } catch (e) {
      console.error(e);
      setToast({ kind: "err", msg: "Publish failed. Check your connection and access, then try again." });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 5000);
    }
  }

  if (!canEdit) return <Gate toolId={toolId} />;
  if (status === "loading") return <Centered>Loading schedule…</Centered>;
  if (status === "missing") return <Centered>No schedule found at <code>bellSchedules/{division}</code>. Run the seed script first.</Centered>;
  if (status === "error")   return <Centered>Couldn’t load the schedule. <button style={s.linkBtn} onClick={load}>Retry</button></Centered>;

  return (
    <div style={s.page}>
      <div style={s.topBar} />
      <div style={s.header}>
        <div>
          <div style={s.title}>Academic Schedule Editor</div>
          <div style={s.subtitle}>
            {config.meta?.displayName || division} · {config.meta?.schoolYear || ""}
            {lastUpdated && <> · last published {lastUpdated.toLocaleString()} {lastUpdatedBy ? `by ${lastUpdatedBy}` : ""}</>}
          </div>
        </div>
        <div style={s.headerActions}>
          <button style={s.ghostBtn} onClick={() => setPreviewOn((v) => !v)}>{previewOn ? "Hide preview" : "Show preview"}</button>
          <button style={s.ghostBtn} onClick={load} disabled={saving}>Reload</button>
          <button
            style={{ ...s.publishBtn, ...(scopedErrors.length || saving ? s.publishBtnDisabled : {}) }}
            onClick={() => publishScope(scope)}
            disabled={!!scopedErrors.length || saving}
          >
            {saving ? "Publishing…" : `Publish ${scopeLabel}`}
          </button>
        </div>
      </div>

      {toast && <div style={{ ...s.toast, ...(toast.kind === "ok" ? s.toastOk : s.toastErr) }}>{toast.msg}</div>}

      {previewOn && (
        <div style={s.previewPanel}>
          <div style={s.previewBar}>
            <span style={s.previewLabel}>Live preview</span>
            <input type="date" style={s.input} value={previewDate} onChange={(e) => setPreviewDate(e.target.value)} />
            <input type="time" style={s.input} value={previewTime} onChange={(e) => setPreviewTime(e.target.value)} />
            <button style={s.linkBtn} onClick={() => { const n = nowAmman(); setPreviewDate(dstr(n)); setPreviewTime(`${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`); }}>Now</button>
            <span style={{ ...s.optNote, marginLeft: "auto" }}>Shows your unpublished edits · pick a school day &amp; time to see the schedule &amp; lunch</span>
          </div>
          <div style={s.previewStage}>
            <BellSchedulePreview division={division} config={config} sharedMenu={sharedMenu} now={previewNow} scale={0.4} />
          </div>
        </div>
      )}

      <ValidationBanner errors={scopedErrors.map((e) => e.msg)} scopeLabel={scopeLabel} />
      {otherErrors.length > 0 && (
        <div style={{ ...s.optNote, marginLeft: 0, marginTop: -10, marginBottom: 14 }}>
          Other sections have {otherErrors.length} unresolved issue{otherErrors.length !== 1 ? "s" : ""} — they won’t block this publish.
        </div>
      )}

      <div style={s.tabs}>
        {[["weekly","Weekly Schedule"],["noschool","No-School Days"],["special","Special Days"],["announce","Announcement"],["menu","Lunch Menu"],["speaker","Speaker"],["event","Event Mode"],["advanced","Rotation & Year"]].map(([k, label]) => (
          <button key={k} style={{ ...s.tab, ...(section === k ? s.tabActive : {}) }} onClick={() => setSection(k)}>{label}</button>
        ))}
      </div>

      <div style={s.body}>
        {section === "weekly"   && <WeeklySection   config={config} update={update} />}
        {section === "noschool" && <NoSchoolSection config={config} update={update} />}
        {section === "special"  && <SpecialSection  config={config} update={update} />}
        {section === "announce" && <AnnounceSection  config={config} update={update} />}
        {section === "menu"     && <MenuSection      config={config} update={update} sharedMenu={sharedMenu} updateShared={updateShared} division={division} />}
        {section === "speaker"  && <SpeakerSection   config={config} update={update} />}
        {section === "event"    && <EventSection     config={config} update={update} />}
        {section === "advanced" && <AdvancedSection config={config} update={update} />}
      </div>

      <div style={s.footNote}>
        Each section publishes on its own — the button writes <b>only that section’s fields</b>, so it never
        overwrites another section’s edits, even if someone else is working in a different tab at the same time.
        The button is disabled while the current section has errors above.
      </div>
    </div>
  );
}

// ─── VALIDATION BANNER ────────────────────────────────────────────────────────
function ValidationBanner({ errors, scopeLabel }) {
  if (!errors.length) return <div style={s.okBanner}>✓ Ready to publish the {scopeLabel} — no problems here.</div>;
  return (
    <div style={s.errBanner}>
      <div style={s.errTitle}>{errors.length} thing{errors.length !== 1 ? "s" : ""} to fix before publishing the {scopeLabel}:</div>
      <ul style={s.errList}>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
    </div>
  );
}

// ─── WEEKLY SECTION ───────────────────────────────────────────────────────────
function WeeklySection({ config, update }) {
  const [dow, setDow] = useState("0");
  const periods = config.weekdayTemplates?.[dow] || [];
  const setPeriods = (next) => update((c) => { c.weekdayTemplates = c.weekdayTemplates || {}; c.weekdayTemplates[dow] = next; });
  return (
    <div>
      <div style={s.dayTabs}>
        {Object.keys(DOW_LABELS).map((k) => (
          <button key={k} style={{ ...s.dayTab, ...(dow === k ? s.dayTabActive : {}) }} onClick={() => setDow(k)}>{DOW_LABELS[k]}</button>
        ))}
      </div>
      <p style={s.hint}>Fri & Sat are non-school days and have no template.</p>
      <PeriodEditor periods={periods} onChange={setPeriods} config={config} />
    </div>
  );
}

// ─── NO-SCHOOL SECTION ────────────────────────────────────────────────────────
function NoSchoolSection({ config, update }) {
  const rows = config.noSchool || [];
  const setRows = (fn) => update((c) => { c.noSchool = fn(clone(c.noSchool || [])); });
  return (
    <div>
      <p style={s.hint}>Days with no classes. They’re skipped in the rotation count and show the “off” card on the displays.</p>
      <div style={s.tableHead}>
        <span style={{ flex: 1 }}>From</span><span style={{ flex: 1 }}>To</span>
        <span style={{ flex: 2 }}>Label</span><span style={{ width: 90 }}>Campus open</span><span style={{ width: 40 }} />
      </div>
      {rows.map((r, i) => (
        <div key={i} style={s.tableRow}>
          <input type="date" style={{ ...s.input, flex: 1 }} value={r.start || ""} onChange={(e) => setRows((a) => { a[i].start = e.target.value; return a; })} />
          <input type="date" style={{ ...s.input, flex: 1 }} value={r.end || ""} onChange={(e) => setRows((a) => { a[i].end = e.target.value; return a; })} />
          <input type="text" style={{ ...s.input, flex: 2 }} placeholder="e.g. Winter Break" value={r.label || ""} onChange={(e) => setRows((a) => { a[i].label = e.target.value; return a; })} />
          <label style={{ width: 90, textAlign: "center" }}>
            <input type="checkbox" checked={!!r.campusOpen} onChange={(e) => setRows((a) => { a[i].campusOpen = e.target.checked; return a; })} />
          </label>
          <button style={s.iconBtn} title="Remove" onClick={() => setRows((a) => { a.splice(i, 1); return a; })}>✕</button>
        </div>
      ))}
      <button style={s.addBtn} onClick={() => setRows((a) => { a.push({ start: "", end: "", label: "", campusOpen: false }); return a; })}>+ Add no-school day</button>
    </div>
  );
}

// ─── SPECIAL DAYS (OVERRIDES) ─────────────────────────────────────────────────
function SpecialSection({ config, update }) {
  const overrides = config.overrides || {};
  const dates = Object.keys(overrides).sort();
  const [newDate, setNewDate] = useState("");

  const setOverride = (date, fn) => update((c) => { c.overrides = c.overrides || {}; c.overrides[date] = fn(clone(c.overrides[date])); });
  const rekey = (oldDate, next) => update((c) => {
    if (!next || next === oldDate || c.overrides[next]) return;
    c.overrides[next] = c.overrides[oldDate]; delete c.overrides[oldDate];
  });
  const removeOverride = (date) => update((c) => { delete c.overrides[date]; });
  const addOverride = () => {
    if (!newDate || overrides[newDate]) return;
    update((c) => { c.overrides = c.overrides || {}; c.overrides[newDate] = { label: "Special Schedule", periods: [] }; });
    setNewDate("");
  };

  return (
    <div>
      <p style={s.hint}>One-off days with a custom bell order (first day, assembly days, exam days). A special day overrides both the weekly template and any no-school entry.</p>

      <div style={s.addRow}>
        <input type="date" style={s.input} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
        <button style={s.addBtn} onClick={addOverride}>+ Add special day</button>
      </div>

      {dates.length === 0 && <div style={s.empty}>No special days defined.</div>}

      {dates.map((date) => {
        const ov = overrides[date];
        return (
          <div key={date} style={s.overrideCard}>
            <div style={s.overrideHead}>
              <input type="date" style={s.input} value={date} onChange={(e) => rekey(date, e.target.value)} />
              <input type="text" style={{ ...s.input, flex: 1 }} placeholder="Label" value={ov.label || ""} onChange={(e) => setOverride(date, (o) => { o.label = e.target.value; return o; })} />
              <button style={s.iconBtn} title="Delete special day" onClick={() => removeOverride(date)}>✕</button>
            </div>
            <div style={s.overrideOpts}>
              <label style={s.optLabel}>
                Rotation day&nbsp;
                <input type="number" min="1" max={(config.rotation?.days || []).length} style={{ ...s.input, width: 64 }}
                  value={ov.rotationDay ?? ""} placeholder="auto"
                  onChange={(e) => setOverride(date, (o) => { const v = e.target.value; if (v === "") delete o.rotationDay; else o.rotationDay = +v; return o; })} />
                <span style={s.optNote}>(blank = use the normal rotation for that date)</span>
              </label>
              <label style={s.optLabel}>
                <input type="checkbox" checked={ov.countsTowardRotation !== false}
                  onChange={(e) => setOverride(date, (o) => { if (e.target.checked) delete o.countsTowardRotation; else o.countsTowardRotation = false; return o; })} />
                &nbsp;Counts toward the rotation
              </label>
            </div>
            <PeriodEditor periods={ov.periods || []} onChange={(next) => setOverride(date, (o) => { o.periods = next; return o; })} config={config} allowExplicitLetter />
          </div>
        );
      })}
    </div>
  );
}

// ─── RICH TEXT EDITOR ─────────────────────────────────────────────────────────
// Dependency-free contentEditable with a tiny B/I/U toolbar. Uncontrolled: the
// initial HTML is set once on mount (give it key={entry.id} so switching entries
// re-mounts with the right content and the caret never jumps). onInput pushes the
// current HTML up. The display sanitizes on render, so we can store raw HTML here.
function RichTextEditor({ html, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (html || "")) ref.current.innerHTML = html || "";
  }, []); // mount-only: content is uncontrolled after init (key={entry.id} re-mounts per entry)
  const exec = (cmd) => {
    try { document.execCommand("styleWithCSS", false, false); } catch { /* older browsers */ }
    document.execCommand(cmd, false); // tag-based (<b>/<i>/<u>) so the display sanitizer keeps it
    if (ref.current) { ref.current.focus(); onChange(ref.current.innerHTML); }
  };
  const Btn = ({ cmd, children }) => (
    <button type="button" style={s.rtBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => exec(cmd)}>{children}</button>
  );
  return (
    <div style={s.rtWrap}>
      <div style={s.rtToolbar}>
        <Btn cmd="bold"><b>B</b></Btn>
        <Btn cmd="italic"><i>I</i></Btn>
        <Btn cmd="underline"><u>U</u></Btn>
        <span style={{ ...s.optNote, marginLeft: 6 }}>Enter = new line</span>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning style={s.rtArea}
        onInput={() => onChange(ref.current.innerHTML)} />
    </div>
  );
}

// ─── ANNOUNCEMENTS (by-day planner) ───────────────────────────────────────────
function AnnounceSection({ config, update }) {
  const [weekStart, setWeekStart] = useState(() => weekSundayOf(nowAmman()));
  const [dayIdx, setDayIdx] = useState(0);
  const [pending, setPending] = useState(null); // {covering} → interrupt/replace choice

  const ann = config.announcements || { enabled: false, entries: [] };
  const entries = ann.entries || [];
  const setAnn = (fn) => update((c) => {
    c.announcements = c.announcements || { enabled: false, entries: [] };
    if (!Array.isArray(c.announcements.entries)) c.announcements.entries = [];
    fn(c.announcements);
  });
  const setEntry = (id, fn) => setAnn((A) => { const e = A.entries.find((x) => x.id === id); if (e) fn(e); });
  const removeEntry = (id) => setAnn((A) => { A.entries = A.entries.filter((x) => x.id !== id); });

  const weekDates = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const date = weekDates[dayIdx];
  const ds = dstr(date);
  const dayStart = ds + "T00:00", dayEnd = ds + "T23:59";

  // All entries stay visible (past, present, future), newest-first, so nothing is
  // ever hidden by the day filter — including a message with a broken date window.
  const sorted = [...entries].sort((a, b) => (b.start || "").localeCompare(a.start || ""));
  const nowStr = (() => { const n = nowAmman(); return `${dstr(n)}T${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`; })();

  const doAdd = (mode, covering) => {
    setAnn((A) => {
      A.entries.push({ id: "a" + Date.now(), html: "", style: "info", start: dayStart, end: dayEnd });
      if (mode === "replace" && covering) { const cov = A.entries.find((x) => x.id === covering.id); if (cov) cov.end = dayStart; }
    });
    setPending(null);
  };
  const onAddClick = () => {
    // A longer span that fully covers this day and continues beyond it.
    const covering = entries.find((e) => e.start < dayStart && e.end > dayEnd);
    if (covering) setPending({ covering });
    else doAdd("new");
  };

  const weekLabel = `${MON_SHORT[weekStart.getMonth()]} ${weekStart.getDate()} – ${weekDates[4].getMonth() !== weekStart.getMonth() ? MON_SHORT[weekDates[4].getMonth()] + " " : ""}${weekDates[4].getDate()}`;
  const dayName = `${DOW_SHORT[date.getDay()]} ${date.getMonth() + 1}/${date.getDate()}`;

  return (
    <div>
      <p style={s.hint}>Plan the bottom banner by day, just like the lunch menu. A message shows during its window; when two overlap, the one that started most recently wins. Every message you create stays in the list below — past ones are kept and marked “Ended” so you can see what ran.</p>

      <label style={{ ...s.optLabel, marginBottom: 10 }}>
        <input type="checkbox" checked={!!ann.enabled} onChange={(e) => setAnn((A) => { A.enabled = e.target.checked; })} />
        &nbsp;Show announcements
      </label>

      <h4 style={s.h4}>Week</h4>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <button style={s.ghostBtn} onClick={() => setWeekStart((w) => addDays(w, -7))}>◀ Prev</button>
        <span style={{ fontWeight: 600, minWidth: 130, textAlign: "center" }}>{weekLabel}</span>
        <button style={s.ghostBtn} onClick={() => setWeekStart((w) => addDays(w, 7))}>Next ▶</button>
        <button style={s.linkBtn} onClick={() => { setWeekStart(weekSundayOf(nowAmman())); setDayIdx(0); }}>This week</button>
      </div>

      <div style={s.dayTabs}>
        {weekDates.map((d, i) => {
          const dds = dstr(d);
          const has = entries.some((e) => (e.start || "").slice(0, 10) <= dds && dds <= (e.end || "").slice(0, 10) && stripTags(e.html));
          return (
            <button key={i} style={{ ...s.dayTab, ...(dayIdx === i ? s.dayTabActive : {}) }} onClick={() => { setDayIdx(i); setPending(null); }}>
              {DOW_SHORT[d.getDay()]} {d.getMonth() + 1}/{d.getDate()}{has ? " •" : ""}
            </button>
          );
        })}
      </div>

      {pending && (
        <div style={{ ...s.overrideCard, borderColor: KA.gold }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>This day is already covered by a longer message</div>
          <div style={{ ...s.optNote, marginLeft: 0 }}>“{stripTags(pending.covering.html) || "(no text yet)"}” — runs until {pending.covering.end.replace("T", " ")}</div>
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button style={s.ghostBtn} onClick={() => doAdd("interrupt", pending.covering)}>Interrupt — resume the old one after</button>
            <button style={s.publishBtn} onClick={() => doAdd("replace", pending.covering)}>Replace — end the old one now</button>
            <button style={s.linkBtn} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}

      {!pending && (
        <div style={s.addRow}>
          <button style={s.addBtn} onClick={onAddClick}>+ Add announcement for {dayName}</button>
        </div>
      )}

      <h4 style={s.h4}>All announcements</h4>
      {sorted.length === 0 && <div style={s.empty}>No announcements yet. Pick a day above and add one.</div>}
      {sorted.map((e) => (
        <EntryEditor key={e.id} entry={e} setEntry={setEntry} removeEntry={removeEntry} nowStr={nowStr} />
      ))}
    </div>
  );
}

function EntryEditor({ entry, setEntry, removeEntry, nowStr }) {
  const [sDate, sTime] = splitDT(entry.start);
  const [eDate, eTime] = splitDT(entry.end);
  const persist = sDate !== eDate;
  const setStart = (d, t) => setEntry(entry.id, (e) => { e.start = joinDT(d, t); });
  const setEnd   = (d, t) => setEntry(entry.id, (e) => { e.end = joinDT(d, t); });
  const togglePersist = (on) => setEntry(entry.id, (e) => {
    const [d] = splitDT(e.start);
    if (on) { const nd = dstr(addDays(new Date(d + "T00:00"), 4)); e.end = nd + "T15:00"; } // default: through the school week
    else { e.end = d + "T23:59"; }
  });

  const valid = DTRE.test(entry.start || "") && DTRE.test(entry.end || "");
  const badWindow = valid && entry.end <= entry.start;
  const status = !valid || badWindow ? { t: "Needs fixing", c: KA.red }
    : nowStr < entry.start ? { t: "Scheduled", c: KA.navy }
    : nowStr < entry.end ? { t: "Showing now", c: KA.green }
    : { t: "Ended", c: "rgba(35,60,95,0.5)" };

  return (
    <div style={{ ...s.overrideCard, ...(badWindow ? { borderColor: KA.errBorder, background: KA.errBg } : {}) }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10 }}>
        <select style={{ ...s.input, width: 200 }} value={entry.style || "info"} onChange={(e) => setEntry(entry.id, (x) => { x.style = e.target.value; })}>
          <option value="info">Info (navy)</option>
          <option value="alert">Alert (red)</option>
          <option value="highlight">Highlight (gold)</option>
        </select>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
          <span style={{ ...s.statusChip, background: status.c }}>{status.t}</span>
          <button style={s.iconBtn} title="Delete announcement" onClick={() => removeEntry(entry.id)}>✕</button>
        </div>
      </div>

      <div style={{ ...s.optNote, marginLeft: 0, marginBottom: 6 }}>
        {sDate || "—"} {sTime} → {persist ? `${eDate || "—"} ${eTime}` : `${sDate || "—"} 23:59`}
        {badWindow && <b style={{ color: KA.red }}> · ends before it starts — fix the dates below</b>}
      </div>

      <h4 style={{ ...s.h4, marginTop: 6 }}>Message</h4>
      <RichTextEditor key={entry.id} html={entry.html} onChange={(h) => setEntry(entry.id, (e) => { e.html = h; })} />

      <h4 style={{ ...s.h4, marginTop: 14 }}>Starts</h4>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input type="date" style={s.input} value={sDate} onChange={(e) => setStart(e.target.value, sTime)} />
        <input type="time" style={s.input} value={sTime} onChange={(e) => setStart(sDate, e.target.value)} />
      </div>

      <label style={{ ...s.optLabel, margin: "14px 0 6px" }}>
        <input type="checkbox" checked={persist} onChange={(e) => togglePersist(e.target.checked)} />
        &nbsp;Keep showing for more than one day
      </label>
      {persist ? (
        <div>
          <div style={{ ...s.h4, marginTop: 4 }}>Disappears</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" style={s.input} value={eDate} onChange={(e) => setEnd(e.target.value, eTime)} />
            <input type="time" style={s.input} value={eTime} onChange={(e) => setEnd(eDate, e.target.value)} />
          </div>
        </div>
      ) : (
        <p style={{ ...s.optNote, marginLeft: 0 }}>Clears at the end of {sDate || "the day"} (23:59). Check the box to pick an end date &amp; time.</p>
      )}
    </div>
  );
}

// ─── LUNCH MENU ───────────────────────────────────────────────────────────────
function MenuSection({ config, update, sharedMenu, updateShared, division }) {
  const [weekStart, setWeekStart] = useState(() => weekSundayOf(nowAmman()));
  const [dayIdx, setDayIdx] = useState(0);

  const linked = (config.lunchMenu?.linked !== false); // default: linked/shared
  const source = linked ? (sharedMenu || {}) : (config.lunchMenu || {});
  const thisSchool  = division === "middleSchool" ? "Middle School" : "Upper School";
  const otherSchool = division === "middleSchool" ? "Upper School" : "Middle School";

  // Apply a mutation to whichever menu is active (shared or this division's own).
  const setSource = (mutator) => {
    if (linked) updateShared((s) => { mutator(s); });
    else update((c) => { c.lunchMenu = { ...(c.lunchMenu || {}) }; mutator(c.lunchMenu); });
  };
  const setLinked = (val) => update((c) => { c.lunchMenu = { ...(c.lunchMenu || {}) }; c.lunchMenu.linked = val; });

  const weekDates = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)); // Sun–Thu
  const date = weekDates[dayIdx];
  const ds = dstr(date);
  const dayEntry = (source.byDate && source.byDate[ds]) || {};
  const items = dayEntry.items || [];

  const setDay  = (fn) => setSource((x) => { x.byDate = { ...(x.byDate || {}) }; x.byDate[ds] = fn(clone(x.byDate[ds] || { items: [] })); });
  const setItems = (fn) => setDay((d) => { d.items = fn(clone(d.items || [])); return d; });

  const weekLabel = `${MON_SHORT[weekStart.getMonth()]} ${weekStart.getDate()} – ${weekDates[4].getMonth() !== weekStart.getMonth() ? MON_SHORT[weekDates[4].getMonth()] + " " : ""}${weekDates[4].getDate()}`;

  return (
    <div>
      <div style={{ ...s.overrideCard, marginTop: 0 }}>
        <label style={s.optLabel}>
          <input type="checkbox" checked={linked} onChange={(e) => setLinked(e.target.checked)} />
          &nbsp;Use the shared school-wide lunch menu
        </label>
        <p style={{ ...s.optNote, marginLeft: 0, marginTop: 6 }}>
          {linked
            ? `On — one menu for both schools. Edits here show on ${thisSchool} and ${otherSchool} displays, and can be changed from either editor.`
            : `Off — ${thisSchool} has its own lunch menu, independent of ${otherSchool}.`}
        </p>
      </div>

      <p style={s.hint}>Set a menu per date. The display shows that date's menu only while school is in session — it clears on weekends and after the last period, and doesn't repeat. Use Prev/Next to load future weeks ahead of time.</p>

      <label style={{ ...s.optLabel, marginBottom: 6 }}>
        <input type="checkbox" checked={!!source.enabled}
          onChange={(e) => setSource((x) => { x.enabled = e.target.checked; })} />
        &nbsp;Show lunch menu{linked ? " (both schools)" : ""}
      </label>
      <label style={{ ...s.optLabel, marginBottom: 6 }}>
        <input type="checkbox" checked={source.showCategories !== false}
          onChange={(e) => setSource((x) => { x.showCategories = e.target.checked; })} />
        &nbsp;Show category labels <span style={{ ...s.optNote }}>(uncheck for plain dish lines under the LUNCH header)</span>
      </label>

      <h4 style={s.h4}>Week</h4>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <button style={s.ghostBtn} onClick={() => { setWeekStart((w) => addDays(w, -7)); }}>◀ Prev</button>
        <span style={{ fontWeight: 600, minWidth: 130, textAlign: "center" }}>{weekLabel}</span>
        <button style={s.ghostBtn} onClick={() => { setWeekStart((w) => addDays(w, 7)); }}>Next ▶</button>
        <button style={s.linkBtn} onClick={() => { setWeekStart(weekSundayOf(nowAmman())); setDayIdx(0); }}>This week</button>
      </div>

      <div style={s.dayTabs}>
        {weekDates.map((d, i) => {
          const filled = (((source.byDate && source.byDate[dstr(d)]) || {}).items || []).some((it) => (it.text || "").trim());
          return (
            <button key={i} style={{ ...s.dayTab, ...(dayIdx === i ? s.dayTabActive : {}) }} onClick={() => setDayIdx(i)}>
              {DOW_SHORT[d.getDay()]} {d.getMonth() + 1}/{d.getDate()}{filled ? " •" : ""}
            </button>
          );
        })}
      </div>

      <h4 style={s.h4}>Heading for {DOW_SHORT[date.getDay()]} {date.getMonth() + 1}/{date.getDate()}</h4>
      <input type="text" style={{ ...s.input, width: 320 }} placeholder="Today's Lunch"
        value={dayEntry.title || ""} onChange={(e) => setDay((d) => { d.title = e.target.value; return d; })} />

      <h4 style={s.h4}>Menu lines</h4>
      <div style={s.pHead}>
        <span style={{ width: 54 }} />
        <span style={{ width: 160 }}>Category</span>
        <span style={{ flex: 1 }}>Dish</span>
        <span style={{ width: 32 }} />
      </div>
      {items.length === 0 && <div style={s.empty}>No menu set for this day.</div>}
      {items.map((it, i) => (
        <div key={i} style={s.pRow}>
          <div style={s.reorder}>
            <button style={s.miniBtn} disabled={i === 0} onClick={() => setItems((a) => { [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; })}>▲</button>
            <button style={s.miniBtn} disabled={i === items.length - 1} onClick={() => setItems((a) => { [a[i + 1], a[i]] = [a[i], a[i + 1]]; return a; })}>▼</button>
          </div>
          <select style={{ ...s.input, width: 160 }} value={it.category || MENU_CATEGORIES[0]}
            onChange={(e) => setItems((a) => { a[i].category = e.target.value; return a; })}>
            {MENU_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="text" style={{ ...s.input, flex: 1 }} placeholder="e.g. Grilled chicken with rice"
            value={it.text || ""} onChange={(e) => setItems((a) => { a[i].text = e.target.value; return a; })} />
          <button style={s.iconBtn} title="Remove line" onClick={() => setItems((a) => { a.splice(i, 1); return a; })}>✕</button>
        </div>
      ))}
      <div style={s.addRow}>
        <button style={s.addBtn} onClick={() => setItems((a) => { a.push({ category: MENU_CATEGORIES[0], text: "" }); return a; })}>+ Add menu line</button>
      </div>
    </div>
  );
}

// ─── FEATURED SPEAKER ─────────────────────────────────────────────────────────
function SpeakerSection({ config, update }) {
  const f = config.featured || {};
  const [uploading, setUploading] = useState(false);
  const set = (fn) => update((c) => { c.featured = { ...(c.featured || {}) }; fn(c.featured); });

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try { const url = await fileToResizedDataUrl(file); set((x) => { x.photo = url; }); }
    catch { window.alert("Couldn't process that image. Try a JPG or PNG."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const persistUntil = !!f.showUntil;
  const [uDate, uTime] = splitDT(f.showUntil || "");
  const togglePersist = (on) => set((x) => {
    if (on) { const d = dstr(addDays(nowAmman(), 7)); x.showUntil = d + "T08:00"; }
    else { delete x.showUntil; }
  });

  return (
    <div>
      <p style={s.hint}>Highlight an upcoming speaker on the left of the displays — photo, name, and a short bio. While it’s on, it takes the place of the lunch menu; the bottom announcement bar keeps working separately.</p>

      <label style={{ ...s.optLabel, marginBottom: 10 }}>
        <input type="checkbox" checked={!!f.enabled} onChange={(e) => set((x) => { x.enabled = e.target.checked; })} />
        &nbsp;Show featured speaker
      </label>

      <h4 style={s.h4}>Photo</h4>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        {f.photo
          ? <img src={f.photo} alt="Speaker" style={s.speakerThumb} />
          : <div style={{ ...s.speakerThumb, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(35,60,95,0.4)", fontSize: 12 }}>No photo</div>}
        <div>
          <label style={{ ...s.addBtn, display: "inline-block" }}>
            {uploading ? "Processing…" : (f.photo ? "Replace photo" : "Upload photo")}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} disabled={uploading} />
          </label>
          {f.photo && <button style={{ ...s.linkBtn, marginLeft: 10 }} onClick={() => set((x) => { delete x.photo; })}>Remove</button>}
          <p style={{ ...s.optNote, marginLeft: 0, marginTop: 6 }}>JPG or PNG — automatically resized and stored with the schedule. A head-and-shoulders photo works best.</p>
        </div>
      </div>

      <h4 style={s.h4}>Label</h4>
      <input type="text" style={{ ...s.input, width: 320 }} placeholder="Upcoming Speaker"
        value={f.eyebrow || ""} onChange={(e) => set((x) => { x.eyebrow = e.target.value; })} />

      <h4 style={s.h4}>Name</h4>
      <input type="text" style={{ ...s.input, width: 420 }} placeholder="e.g. Dr. Jane Smith"
        value={f.name || ""} onChange={(e) => set((x) => { x.name = e.target.value; })} />

      <h4 style={s.h4}>Short bio</h4>
      <textarea style={{ ...s.input, width: "100%", minHeight: 90, resize: "vertical", boxSizing: "border-box" }}
        placeholder="A sentence or two — role, topic, when they're speaking."
        value={f.bio || ""} onChange={(e) => set((x) => { x.bio = e.target.value; })} />

      <label style={{ ...s.optLabel, margin: "14px 0 6px" }}>
        <input type="checkbox" checked={persistUntil} onChange={(e) => togglePersist(e.target.checked)} />
        &nbsp;Automatically hide after a date &amp; time
      </label>
      {persistUntil ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="date" style={s.input} value={uDate} onChange={(e) => set((x) => { x.showUntil = joinDT(e.target.value, uTime); })} />
          <input type="time" style={s.input} value={uTime} onChange={(e) => set((x) => { x.showUntil = joinDT(uDate, e.target.value); })} />
        </div>
      ) : (
        <p style={{ ...s.optNote, marginLeft: 0 }}>Stays up until you turn it off. Check the box to have it clear itself automatically.</p>
      )}
    </div>
  );
}

// ─── EVENT MODE ───────────────────────────────────────────────────────────────
// Turns the whole display for this division into an events board on any day: a
// weekend activities list, a one-off event, and/or a full flyer. Writes only the
// `eventMode` field on publish, independent of every other section.
function EventSection({ config, update }) {
  const e = config.eventMode || {};
  const events = e.events || [];
  const [uploading, setUploading] = useState(false);
  const set = (fn) => update((c) => { c.eventMode = { ...(c.eventMode || {}) }; fn(c.eventMode); });
  const setEvents = (fn) => set((x) => { x.events = fn(clone(x.events || [])); });

  const posterIsUrl = !!(e.poster && /^https?:\/\//.test(e.poster));

  const onPoster = async (ev) => {
    const file = ev.target.files && ev.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await fileToResizedDataUrl(file, 1100, 0.82); // larger than a speaker photo — it's a flyer
      if (url.length > 900000) {
        window.alert("That image is too large to store inline with the schedule (keep it well under ~1 MB after resizing). Try a smaller image, or host it somewhere and paste the image URL instead.");
      } else {
        set((x) => { x.poster = url; });
      }
    } catch { window.alert("Couldn't process that image. Try a JPG or PNG, or paste a URL instead."); }
    finally { setUploading(false); ev.target.value = ""; }
  };

  return (
    <div>
      <p style={s.hint}>Turn the whole display into an events board on <b>any</b> day — a weekend activities list, a one-off event, and/or a full flyer. While it’s on, it replaces the schedule for this division; the clock, date, and announcement bar keep working. Turn it off to return to the normal schedule. This is per division — the other school is unaffected.</p>

      <label style={{ ...s.optLabel, marginBottom: 10 }}>
        <input type="checkbox" checked={!!e.enabled} onChange={(ev) => set((x) => { x.enabled = ev.target.checked; })} />
        &nbsp;<b>Turn on event mode</b>{e.enabled ? " — once published, this division's displays show the events board" : ""}
      </label>

      <h4 style={s.h4}>Heading</h4>
      <input type="text" style={{ ...s.input, width: 420 }} placeholder="This Weekend at King's"
        value={e.title || ""} onChange={(ev) => set((x) => { x.title = ev.target.value; })} />

      <h4 style={s.h4}>Subheading</h4>
      <input type="text" style={{ ...s.input, width: 420 }} placeholder="Student Activities"
        value={e.subtitle || ""} onChange={(ev) => set((x) => { x.subtitle = ev.target.value; })} />

      <h4 style={s.h4}>Flyer / poster (optional)</h4>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        {e.poster
          ? <img src={e.poster} alt="Poster" style={{ ...s.speakerThumb, width: 120, height: 150, objectFit: e.posterFit === "cover" ? "cover" : "contain", background: KA.white }} />
          : <div style={{ ...s.speakerThumb, width: 120, height: 150, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(35,60,95,0.4)", fontSize: 12 }}>No poster</div>}
        <div style={{ flex: 1, minWidth: 260 }}>
          <label style={{ ...s.addBtn, display: "inline-block" }}>
            {uploading ? "Processing…" : (e.poster ? "Replace image" : "Upload image")}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={onPoster} disabled={uploading} />
          </label>
          {e.poster && <button style={{ ...s.linkBtn, marginLeft: 10 }} onClick={() => set((x) => { delete x.poster; })}>Remove</button>}
          <p style={{ ...s.optNote, marginLeft: 0, marginTop: 6 }}>Upload a flyer (JPG/PNG, resized and stored with the schedule) or paste a hosted image URL below. With events listed, the flyer sits beside the list; with no events it fills the screen.</p>
          <input type="text" style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }} placeholder="…or paste an image URL (https://…)"
            value={posterIsUrl ? e.poster : ""} onChange={(ev) => set((x) => { const v = ev.target.value.trim(); if (v) x.poster = v; else if (posterIsUrl) delete x.poster; })} />
          <label style={{ ...s.optLabel, marginTop: 8 }}>
            Fit&nbsp;
            <select style={{ ...s.input, width: 180 }} value={e.posterFit || "contain"} onChange={(ev) => set((x) => { x.posterFit = ev.target.value; })}>
              <option value="contain">Show the whole flyer</option>
              <option value="cover">Fill the space (may crop)</option>
            </select>
          </label>
        </div>
      </div>

      <h4 style={s.h4}>Events</h4>
      <p style={{ ...s.optNote, marginLeft: 0, marginBottom: 10 }}>Date and times are optional. An event dated today with a start time gets a live NOW / NEXT tag and dims once it’s over. Leave the date blank for an always-listed item.</p>

      {events.length === 0 && <div style={s.empty}>No events yet.</div>}

      {events.map((ev, i) => (
        <div key={ev.id || i} style={s.overrideCard}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <div style={s.reorder}>
              <button style={s.miniBtn} disabled={i === 0} onClick={() => setEvents((a) => { [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; })}>▲</button>
              <button style={s.miniBtn} disabled={i === events.length - 1} onClick={() => setEvents((a) => { [a[i + 1], a[i]] = [a[i], a[i + 1]]; return a; })}>▼</button>
            </div>
            <input type="text" style={{ ...s.input, flex: 1 }} placeholder="Event title (e.g. Movie Night)"
              value={ev.title || ""} onChange={(e2) => setEvents((a) => { a[i].title = e2.target.value; return a; })} />
            <button style={s.iconBtn} title="Remove event" onClick={() => setEvents((a) => { a.splice(i, 1); return a; })}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
            <label style={s.optLabel}>Date&nbsp;
              <input type="date" style={s.input} value={ev.date || ""} onChange={(e2) => setEvents((a) => { if (e2.target.value) a[i].date = e2.target.value; else delete a[i].date; return a; })} />
            </label>
            <label style={s.optLabel}>Start&nbsp;
              <input type="time" style={s.input} value={ev.start || ""} onChange={(e2) => setEvents((a) => { if (e2.target.value) a[i].start = e2.target.value; else delete a[i].start; return a; })} />
            </label>
            <label style={s.optLabel}>End&nbsp;
              <input type="time" style={s.input} value={ev.end || ""} onChange={(e2) => setEvents((a) => { if (e2.target.value) a[i].end = e2.target.value; else delete a[i].end; return a; })} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <input type="text" style={{ ...s.input, flex: 1, minWidth: 200 }} placeholder="Location (e.g. Black Box Theater)"
              value={ev.location || ""} onChange={(e2) => setEvents((a) => { a[i].location = e2.target.value; return a; })} />
            <input type="text" style={{ ...s.input, flex: 1, minWidth: 200 }} placeholder="Note (optional — e.g. Popcorn provided)"
              value={ev.note || ""} onChange={(e2) => setEvents((a) => { a[i].note = e2.target.value; return a; })} />
          </div>
        </div>
      ))}

      <div style={s.addRow}>
        <button style={s.addBtn} onClick={() => setEvents((a) => { a.push({ id: `e${Date.now()}`, title: "", location: "", note: "" }); return a; })}>+ Add event</button>
      </div>
    </div>
  );
}

// ─── ADVANCED (ROTATION & YEAR) ───────────────────────────────────────────────
function AdvancedSection({ config, update }) {
  const rot = config.rotation || { letters: [], days: [] };
  const year = config.year || { start: "", end: "", semesters: [] };
  return (
    <div>
      <p style={s.hint}>Rarely changed. The rotation grid must match the official rotation calendar; each row is one rotation day, one letter per block in order.</p>

      <h4 style={s.h4}>Rotation letters</h4>
      <input type="text" style={{ ...s.input, width: 320 }} value={(rot.letters || []).join(",")}
        onChange={(e) => update((c) => { c.rotation.letters = e.target.value.split(",").map((x) => x.trim()).filter(Boolean); })} />

      <h4 style={s.h4}>Rotation grid</h4>
      {(rot.days || []).map((row, i) => (
        <div key={i} style={s.rotRow}>
          <span style={s.rotLabel}>Day {i + 1}</span>
          <input type="text" style={{ ...s.input, width: 160, letterSpacing: 3, fontFamily: "monospace" }}
            value={row} maxLength={config.meta?.blocksPerDay ?? 5}
            onChange={(e) => update((c) => { c.rotation.days[i] = e.target.value.toUpperCase(); })} />
        </div>
      ))}

      <h4 style={s.h4}>School year</h4>
      <div style={s.addRow}>
        <label style={s.optLabel}>Start&nbsp;<input type="date" style={s.input} value={year.start || ""} onChange={(e) => update((c) => { c.year.start = e.target.value; })} /></label>
        <label style={s.optLabel}>End&nbsp;<input type="date" style={s.input} value={year.end || ""} onChange={(e) => update((c) => { c.year.end = e.target.value; })} /></label>
      </div>

      <h4 style={s.h4}>Semesters (rotation resets to Day 1 at each anchor)</h4>
      {(year.semesters || []).map((sem, i) => (
        <div key={i} style={s.tableRow}>
          <input type="text" style={{ ...s.input, flex: 1 }} placeholder="Name" value={sem.name || ""} onChange={(e) => update((c) => { c.year.semesters[i].name = e.target.value; })} />
          <input type="date" style={{ ...s.input, flex: 1 }} value={sem.anchor || ""} onChange={(e) => update((c) => { c.year.semesters[i].anchor = e.target.value; })} />
          <button style={s.iconBtn} title="Remove" onClick={() => update((c) => { c.year.semesters.splice(i, 1); })}>✕</button>
        </div>
      ))}
      <button style={s.addBtn} onClick={() => update((c) => { c.year.semesters = c.year.semesters || []; c.year.semesters.push({ name: "", anchor: "" }); })}>+ Add semester</button>
    </div>
  );
}

// ─── PERIOD EDITOR (reused for weekday templates + overrides) ──────────────────
function PeriodEditor({ periods, onChange, config, allowExplicitLetter = false }) {
  const bpd = config.meta?.blocksPerDay ?? 5;
  const letters = config.rotation?.letters || [];
  const edit = (fn) => onChange((() => { const a = clone(periods); fn(a); return a; })());

  const blockValue = (p) => (p.blockLetter ? `letter:${p.blockLetter}` : p.slot != null ? `slot:${p.slot}` : "");
  const setBlock = (i, v) => edit((a) => {
    delete a[i].slot; delete a[i].blockLetter;
    if (v.startsWith("slot:")) a[i].slot = +v.slice(5);
    else if (v.startsWith("letter:")) a[i].blockLetter = v.slice(7);
  });

  return (
    <div>
      <div style={s.pHead}>
        <span style={{ width: 54 }} />
        <span style={{ flex: 2 }}>Label</span>
        <span style={{ width: 96 }}>Start</span>
        <span style={{ width: 96 }}>End</span>
        <span style={{ width: 120 }}>Type</span>
        <span style={{ width: 150 }}>Block</span>
        <span style={{ width: 32 }} />
      </div>

      {periods.map((p, i) => (
        <div key={i} style={s.pRow}>
          <div style={s.reorder}>
            <button style={s.miniBtn} disabled={i === 0} onClick={() => edit((a) => { [a[i - 1], a[i]] = [a[i], a[i - 1]]; })}>▲</button>
            <button style={s.miniBtn} disabled={i === periods.length - 1} onClick={() => edit((a) => { [a[i + 1], a[i]] = [a[i], a[i + 1]]; })}>▼</button>
          </div>
          <input type="text" style={{ ...s.input, flex: 2 }} value={p.label || ""} onChange={(e) => edit((a) => { a[i].label = e.target.value; })} />
          <input type="time" style={{ ...s.input, width: 96 }} value={p.start || ""} onChange={(e) => edit((a) => { a[i].start = e.target.value; })} />
          <input type="time" style={{ ...s.input, width: 96 }} value={p.end || ""} onChange={(e) => edit((a) => { a[i].end = e.target.value; })} />
          <select style={{ ...s.input, width: 120 }} value={p.type || "class"} onChange={(e) => edit((a) => { a[i].type = e.target.value; if (e.target.value !== "class") { delete a[i].slot; delete a[i].blockLetter; } })}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {p.type === "class" ? (
            <select style={{ ...s.input, width: 150 }} value={blockValue(p)} onChange={(e) => setBlock(i, e.target.value)}>
              <option value="">— choose —</option>
              {Array.from({ length: bpd }, (_, n) => <option key={n} value={`slot:${n}`}>Slot {n + 1} (rotation)</option>)}
              {allowExplicitLetter && letters.map((l) => <option key={l} value={`letter:${l}`}>Fixed: {l}</option>)}
            </select>
          ) : <span style={{ width: 150 }} />}
          <button style={s.iconBtn} title="Remove period" onClick={() => edit((a) => { a.splice(i, 1); })}>✕</button>
        </div>
      ))}

      <div style={s.addRow}>
        <button style={s.addBtn} onClick={() => edit((a) => { a.push({ id: `p${Date.now()}`, label: "", start: "", end: "", type: "class" }); })}>+ Add period</button>
      </div>
    </div>
  );
}

// ─── SMALL SHELLS ─────────────────────────────────────────────────────────────
function Gate({ toolId }) {
  return <Centered>You don’t have access to the Academic Schedule Editor. Ask a superadmin to grant the <code>{toolId}</code> tool.</Centered>;
}
function Centered({ children }) {
  return <div style={s.centered}>{children}</div>;
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const s = {
  page:{ maxWidth:1080, margin:"0 auto", padding:"0 20px 60px", color:KA.navy, fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif" },
  topBar:{ height:6, background:KA.red, margin:"0 -20px 0" },
  header:{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:16, padding:"22px 0 16px", borderBottom:`1px solid rgba(162,145,100,0.3)` },
  title:{ fontSize:26, fontWeight:300, letterSpacing:1, fontFamily:"Georgia,serif" },
  subtitle:{ fontSize:13, color:"rgba(35,60,95,0.6)", marginTop:4 },
  headerActions:{ display:"flex", gap:10 },
  ghostBtn:{ background:"transparent", border:`1px solid rgba(35,60,95,0.3)`, color:KA.navy, padding:"9px 16px", borderRadius:4, cursor:"pointer", fontSize:14 },
  publishBtn:{ background:KA.red, border:"none", color:KA.white, padding:"9px 20px", borderRadius:4, cursor:"pointer", fontSize:14, fontWeight:600 },
  publishBtnDisabled:{ background:"rgba(35,60,95,0.25)", cursor:"not-allowed" },
  linkBtn:{ background:"none", border:"none", color:KA.red, cursor:"pointer", textDecoration:"underline", fontSize:"inherit" },
  toast:{ padding:"10px 16px", borderRadius:4, margin:"14px 0", fontSize:14 },
  toastOk:{ background:"rgba(46,125,91,0.12)", color:KA.green, border:`1px solid rgba(46,125,91,0.4)` },
  toastErr:{ background:KA.errBg, color:KA.red, border:`1px solid ${KA.errBorder}` },
  okBanner:{ background:"rgba(46,125,91,0.1)", color:KA.green, border:`1px solid rgba(46,125,91,0.35)`, padding:"10px 16px", borderRadius:4, margin:"16px 0", fontSize:14 },
  errBanner:{ background:KA.errBg, border:`1px solid ${KA.errBorder}`, borderRadius:4, padding:"12px 16px", margin:"16px 0" },
  errTitle:{ color:KA.red, fontWeight:600, fontSize:14, marginBottom:6 },
  errList:{ margin:0, paddingLeft:20, color:KA.navy, fontSize:13, lineHeight:1.6 },
  tabs:{ display:"flex", gap:4, borderBottom:`1px solid rgba(162,145,100,0.3)`, marginBottom:20, flexWrap:"wrap" },
  tab:{ background:"transparent", border:"none", borderBottom:"3px solid transparent", padding:"10px 16px", cursor:"pointer", fontSize:14, color:"rgba(35,60,95,0.6)" },
  tabActive:{ color:KA.navy, fontWeight:600, borderBottom:`3px solid ${KA.red}` },
  body:{ minHeight:200 },
  dayTabs:{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" },
  dayTab:{ background:KA.offWhite, border:`1px solid rgba(162,145,100,0.35)`, padding:"7px 14px", borderRadius:20, cursor:"pointer", fontSize:13 },
  dayTabActive:{ background:KA.navy, color:KA.white, border:`1px solid ${KA.navy}` },
  hint:{ fontSize:13, color:"rgba(35,60,95,0.6)", margin:"0 0 14px" },
  pHead:{ display:"flex", alignItems:"center", gap:8, padding:"0 4px 6px", fontSize:11, textTransform:"uppercase", letterSpacing:1, color:"rgba(35,60,95,0.5)" },
  pRow:{ display:"flex", alignItems:"center", gap:8, padding:"5px 4px" },
  reorder:{ width:54, display:"flex", gap:2 },
  input:{ padding:"7px 8px", border:`1px solid rgba(35,60,95,0.25)`, borderRadius:4, fontSize:13, color:KA.navy, background:KA.white, boxSizing:"border-box" },
  iconBtn:{ width:32, height:32, border:"none", background:"transparent", color:"rgba(35,60,95,0.5)", cursor:"pointer", fontSize:14, borderRadius:4 },
  miniBtn:{ width:24, height:22, border:`1px solid rgba(35,60,95,0.2)`, background:KA.white, color:KA.navy, cursor:"pointer", fontSize:9, borderRadius:3, padding:0 },
  addRow:{ display:"flex", gap:10, alignItems:"center", marginTop:12 },
  addBtn:{ background:"transparent", border:`1px dashed rgba(35,60,95,0.35)`, color:KA.navy, padding:"8px 16px", borderRadius:4, cursor:"pointer", fontSize:13 },
  tableHead:{ display:"flex", gap:8, padding:"0 4px 6px", fontSize:11, textTransform:"uppercase", letterSpacing:1, color:"rgba(35,60,95,0.5)" },
  tableRow:{ display:"flex", gap:8, alignItems:"center", padding:"5px 4px" },
  empty:{ padding:"20px 4px", color:"rgba(35,60,95,0.5)", fontStyle:"italic", fontSize:14 },
  overrideCard:{ border:`1px solid rgba(162,145,100,0.35)`, borderRadius:6, padding:16, margin:"14px 0", background:KA.offWhite },
  overrideHead:{ display:"flex", gap:10, alignItems:"center", marginBottom:10 },
  overrideOpts:{ display:"flex", gap:24, alignItems:"center", flexWrap:"wrap", marginBottom:12, fontSize:13 },
  optLabel:{ display:"flex", alignItems:"center", fontSize:13 },
  optNote:{ marginLeft:8, color:"rgba(35,60,95,0.5)", fontSize:12 },
  h4:{ fontSize:13, textTransform:"uppercase", letterSpacing:1, color:KA.red, margin:"22px 0 8px" },
  rotRow:{ display:"flex", alignItems:"center", gap:12, padding:"3px 0" },
  rotLabel:{ width:60, fontSize:13, color:"rgba(35,60,95,0.6)" },
  footNote:{ marginTop:28, padding:"14px 16px", background:KA.offWhite, borderRadius:6, fontSize:13, color:"rgba(35,60,95,0.7)", border:`1px solid rgba(162,145,100,0.3)` },
  centered:{ maxWidth:600, margin:"80px auto", textAlign:"center", color:KA.navy, fontSize:16, lineHeight:1.6, fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif" },
  rtWrap:{ border:`1px solid rgba(35,60,95,0.25)`, borderRadius:4, overflow:"hidden", background:KA.white },
  rtToolbar:{ display:"flex", alignItems:"center", gap:4, padding:6, background:KA.offWhite, borderBottom:`1px solid rgba(35,60,95,0.15)` },
  rtBtn:{ width:30, height:28, border:`1px solid rgba(35,60,95,0.2)`, background:KA.white, color:KA.navy, borderRadius:3, cursor:"pointer", fontSize:14, lineHeight:1, padding:0 },
  rtArea:{ minHeight:64, padding:"9px 11px", fontSize:14, color:KA.navy, outline:"none", lineHeight:1.4 },
  previewPanel:{ border:`1px solid rgba(162,145,100,0.4)`, borderRadius:8, background:KA.offWhite, padding:12, margin:"14px 0 4px" },
  previewBar:{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:10 },
  previewLabel:{ fontSize:12, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:KA.red },
  previewStage:{ display:"flex", justifyContent:"center", overflow:"auto" },
  statusChip:{ fontSize:11, fontWeight:700, letterSpacing:1, textTransform:"uppercase", color:KA.white, padding:"3px 9px", borderRadius:3, whiteSpace:"nowrap" },
  speakerThumb:{ width:96, height:96, borderRadius:8, objectFit:"cover", border:`1px solid rgba(162,145,100,0.5)`, background:KA.offWhite, flexShrink:0 },
};